import { useMemo } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { useCompany } from '@/contexts/CompanyContext';
import { DEFAULT_RE_DISPLAY_AFTER_DAYS, useFeedbackConfig } from '@/providers/FeedbackConfigProvider';
import type { FeedbackCollectorParams } from '@/types/feedbackCollector';

/**
 * Custom hook to build the feedback collector component parameters.
 *
 * Sources userId from AuthProvider, customerName from CompanyContext,
 * and feedback settings from FeedbackConfigProvider.
 *
 * Stabilises the returned object reference so downstream components do not
 * re-render (and flicker) when the parent tree re-renders with identical
 * context values.
 *
 * @param feature - The feature name to collect feedback for (e.g., "Planner", "Heatmap")
 * @returns The oc-component parameters, or null if feedback is not configured
 */
export function useFeedbackCollectorParams(feature: string): FeedbackCollectorParams | null {
  const { userProfile, instanceId, evoOrganisationId } = useAuth();
  const { currentCompany } = useCompany();
  const config = useFeedbackConfig();

  const email = userProfile?.email ?? 'Unknown';
  const companyName = currentCompany?.name ?? instanceId ?? 'Unknown';
  const orgId = evoOrganisationId ?? instanceId ?? '';

  const accountToken = config?.accountToken;
  const apiHost = config?.apiHost;
  const reDisplayAfterDays = config?.reDisplayAfterDays ?? DEFAULT_RE_DISPLAY_AFTER_DAYS;
  const mfeRegistryBase = config?.mfeRegistryBase;
  const mfeVersion = config?.mfeVersion;
  const product = config?.product;

  return useMemo(() => {
    if (!accountToken || !apiHost || !mfeRegistryBase || !product) return null;

    return {
      feature,
      accountToken,
      apiHost,
      reDisplayAfterDays,
      mfeRegistryBase,
      mfeVersion,
      customerName: companyName,
      userId: email,
      orgId,
      product,
    };
  }, [
    accountToken,
    apiHost,
    companyName,
    email,
    feature,
    mfeRegistryBase,
    mfeVersion,
    orgId,
    product,
    reDisplayAfterDays,
  ]);
}
